import PropTypes from 'prop-types';
import React, { useEffect, useMemo, memo } from 'react';
import { Map } from 'immutable';
import classNames from 'classnames';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';
import { createPortal } from 'react-dom';

import { useSharedPusher, CHANNEL_SCREEN_POP_ENDED } from 'src/hooks/useSharedPusher';
import { Spinner } from 'src/components/helpers/Spinner';
import { IconButton } from 'src/components/helpers/IconButton';
import Table, { Column, Row } from '../../helpers/Table';
import uniqueId from '../../../utils/uniqueId';
import { noop } from '../../../utils/noop';

import style from './style.module.css';

const noWrap = v => v;

const ROW_HANDLER_PROPS = ['onDoubleClick'];

const ContextMenuWrapper = (onClick, extraWrap = noWrap) => (value, data) => {
  const id = uniqueId();

  return (
    <div>
      <ContextMenuTrigger id={id}>{extraWrap(value, data)}</ContextMenuTrigger>

      <ContextMenu id={id}>
        <MenuItem data={data.toJS()} onClick={onClick}>
          <i className="fa fa-remove" /> Delete
        </MenuItem>
      </ContextMenu>
    </div>
  );
};

const wrapBacklogged = (text, data, backloggedActivitiesList) => {
  const activity = data.getIn(['activityMap', 'text'], '');

  if (data.get('backlogged', false) && !data.get('completed', false) && backloggedActivitiesList.includes(activity))
    return (
      <span>
        <i className="fa fa-clock-o" /> {text}
      </span>
    );

  return <span>{text}</span>;
};

/**
 * Stateless component for contact's activities.
 * Display table where rows are activities.
 *
 * @link http://screencloud.net/v/jS6q on page (right top part)
 * @link http://screencloud.net/v/7yc2 component itself
 * @param props {Object}.
 * @param props.events {Immutable.List} List with activities info. If now activities
 *     are on contact - should be an empty List.
 */
const CompanyActivities = ({
  backloggedActivities,
  events,
  isDuplicateCompany,
  screenPopChannel,
  onReloadEvents,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onMouseEnter = noop,
  onMouseLeave = noop,
  user,
}) => {
  const backloggedActivitiesList = backloggedActivities.toJS();
  const { subscribe } = useSharedPusher(user);

  useEffect(() => {
    if (!screenPopChannel) {
      return;
    }

    const channel = screenPopChannel.get('name');

    subscribe(channel, CHANNEL_SCREEN_POP_ENDED, () => {
      onReloadEvents();
    });
  }, [screenPopChannel]);

  /**
   * Var for activity rows.
   *
   * @type {React.Component[]}
   */
  const allEvents = events.get('all');

  // onDeleteEvent is used by the column valueWrapper, it is listed so that rows re-render when it changes too.
  const rows = useMemo(
    () =>
      allEvents.map(eventData => (
        <Row
          key={eventData.get('id')}
          checkAlso={ROW_HANDLER_PROPS}
          className={getRowClassName(eventData)}
          data={eventData}
          onDoubleClick={event => onEditEvent(event, eventData)}
          onMouseEnter={event => onMouseEnter(event, eventData.get('id'))}
          onMouseLeave={event => onMouseLeave(event, eventData.get('id'))}
        />
      )),
    [allEvents, onEditEvent, onDeleteEvent, onMouseEnter, onMouseLeave],
  );

  const dateTitle = (
    <span className={style.dateTitle} onClick={onAddEvent}>
      <IconButton icon="plus" size="sm" title="Add activity" variant="primary" />
      Date
    </span>
  );

  const current = events.get('current');
  const tooltipPosition = {
    top: current.get('tooltipTop'),
    left: current.get('tooltipLeft'),
  };

  const container = (
    <div className={style.tooltip} style={tooltipPosition}>
      {current.get('tooltipText')}
    </div>
  );
  const portal = createPortal(container, document.querySelector('body'));
  const tooltipPortal = current.get('showTooltip') && portal;

  const isLoading = events.get('isListLoading');

  return (
    <div className="companyActivities flexWrapper flexItem">
      <Table bodyClassName="flexItem" className={classNames(style.dates, 'table table-striped table-hover flexItem flexWrapper')}>
        <Column
          field="dateString"
          title={dateTitle}
          valueWrapper={isDuplicateCompany ? undefined : ContextMenuWrapper(onDeleteEvent)}
        />
        <Column
          field="userName"
          title="Name"
          valueWrapper={isDuplicateCompany ? undefined : ContextMenuWrapper(onDeleteEvent)}
        />
        <Column
          field="fullActivity"
          title="Activity"
          valueWrapper={
            isDuplicateCompany
              ? undefined
              : ContextMenuWrapper(onDeleteEvent, (text, data) => wrapBacklogged(text, data, backloggedActivitiesList))
          }
        />
        {rows}
      </Table>
      {isLoading && <Spinner />}
      {tooltipPortal}
    </div>
  );
};

CompanyActivities.propTypes = {
  events: PropTypes.instanceOf(Map).isRequired,
  isDuplicateCompany: PropTypes.bool.isRequired,
};

export default memo(CompanyActivities);

function getRowClassName(data) {
  const level = data.get('warnLevel');

  return classNames('clickable', {
    info: level === 0 || level === 1,
    warning: level === 2,
    danger: level === 3,
    'text-bold': data.get('isMarked'),
  });
}
